import { Suspense, useEffect } from "react";
import { Navigate, Route, Routes, useNavigate } from "react-router-dom";
import TopBarProgress from "react-topbar-progress-indicator";

import { getCSSVariableValue } from "../../_metronic/assets/ts/_utils";
import type { WithChildren } from "../../_metronic/helpers";
import { LoadingOverlay } from "../components/LoadingOverlay/LoadingOverlay";
import { useOrganizationContext } from "../contexts/organizations/organizationsContext";
import { useOrganizations } from "../hooks/reactQueryHooks/useOrganization";
import { MasterLayout } from "../layout/MasterLayout";
import { AccountWrapper } from "../pages/accounts/AccountWrapper";
import { ApiKeyWrapper } from "../pages/apikey/ApiKeyWrapper";
import { ApplicationsWrapper } from "../pages/applications/ApplicationsWrapper";
import { ApplicationsDetailsWrapper } from "../pages/applicationsDetails/ApplicationsDetailsWrapper";
import { BillingWrapper } from "../pages/billing/BillingWrapper";
import { CacheWrapper } from "../pages/cache/CacheWrapper";
import { ManageCacheItemWrapper } from "../pages/manageCache/ManageCacheItemWrapper";
import { MembersWrapper } from "../pages/members/MembersWrapper";
import { MentorsWrapper } from "../pages/mentors/MentorsWrapper";
import { SessionWrapper } from "../pages/mentors/session/SessionWrapper";
import { OnboardingTouchcastPage } from "../pages/onboarding/OnboardingTouchcastPage";
import { OverviewWrapper } from "../pages/overview/OverviewWrapper";
import { PlaygroundWrapper } from "../pages/playground/PlaygroundWrapper";
import { RequestsWrapper } from "../pages/requests/RequestsWrapper";
import { SettingsWrapper } from "../pages/settings/SettingsWrapper";
import { ShieldWrapper } from "../pages/shiled/ShieldWrapper";
import { UsageWrapper } from "../pages/usage/UsageWrapper";

const PrivateRoutes = () => {
  const { setOrganizations, isOwner } = useOrganizationContext();

  const res = useOrganizations();

  useEffect(() => {
    res
      .refetch()
      .then(data => {
        if (data.isSuccess) {
          setOrganizations(data.data);
        }
      })
      .catch(console.error);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (res.isLoading) {
    return <LoadingOverlay />;
  }

  return (
    <Routes>
      <Route element={<MasterLayout />}>
        <Route
          path="account/*"
          element={
            <SuspensedView>
              {/* <AccountPage /> */}
              <AccountWrapper />
            </SuspensedView>
          }
        />
        <Route
          path="/overview"
          element={
            <SuspensedView>
              <OverviewWrapper />
            </SuspensedView>
          }
        />
        <Route
          path="projects"
          element={
            <SuspensedView>
              <ApplicationsWrapper />
            </SuspensedView>
          }
        />
        <Route
          path="projects/:projId"
          element={
            <SuspensedView>
              <ApplicationsDetailsWrapper />
            </SuspensedView>
          }
        />

        <Route
          path="/organization/keys"
          element={
            <SuspensedView>
              <ApiKeyWrapper />
            </SuspensedView>
          }
        />
        <Route
          path="/organization/settings"
          element={
            <SuspensedView>
              <SettingsWrapper />
            </SuspensedView>
          }
        />
        <Route
          path="/organization/members/*"
          element={
            <SuspensedView>
              <MembersWrapper />
            </SuspensedView>
          }
        />
        <Route
          path="/safety-compliance/cache"
          element={
            <SuspensedView>
              <CacheWrapper />
            </SuspensedView>
          }
        />
        <Route
          path="/safety-compliance/cache/:projId/:id/*"
          element={
            <SuspensedView>
              <ManageCacheItemWrapper />
            </SuspensedView>
          }
        />
        <Route
          path="/safety-compliance/shield"
          element={
            <SuspensedView>
              <ShieldWrapper />
            </SuspensedView>
          }
        />
        {isOwner && (
          <Route
            path="/organization/billing"
            element={
              <SuspensedView>
                <BillingWrapper />
              </SuspensedView>
            }
          />
        )}

        <Route
          path="/organization/usage"
          element={
            <SuspensedView>
              <UsageWrapper />
            </SuspensedView>
          }
        />
        <Route
          path="/monitoring/requests"
          element={
            <SuspensedView>
              <RequestsWrapper />
            </SuspensedView>
          }
        />
        <Route
          path="/playground"
          element={
            <SuspensedView>
              <PlaygroundWrapper />
            </SuspensedView>
          }
        />
        <Route
          path="/mentors"
          element={
            <SuspensedView>
              <MentorsWrapper />
            </SuspensedView>
          }
        />
        <Route
          path="/session/:mentorId"
          element={
            <SuspensedView>
              <SessionWrapper />
            </SuspensedView>
          }
        />

        {/* Page Not Found */}
        <Route path="*" element={<Navigate to="/error/404" />} />
      </Route>
      <Route path="get-started" element={<OnboardingTouchcastPage />} />
    </Routes>
  );
};

const SuspensedView: React.FC<WithChildren> = ({ children }) => {
  const baseColor = getCSSVariableValue("--bs-primary");
  TopBarProgress.config({
    barColors: {
      "0": baseColor,
    },
    barThickness: 1,
    shadowBlur: 5,
  });
  return <Suspense fallback={<TopBarProgress />}>{children}</Suspense>;
};

export { PrivateRoutes };
